import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BlockedAccountScreen } from './BlockedAccountScreen';
import { TitleBar } from './TitleBar';
import '../styles/RequireAuth.css';

/**
 * RequireAuth - Route guard component
 *
 * Controls access to protected content based on authentication status:
 * - loading → Shows loading spinner
 * - unauth → Redirects to /login (preserves original path)
 * - blocked → Shows BlockedAccountScreen (inactive account)
 * - unverified → Redirects to /verify-email
 * - auth → Renders children (protected content)
 */

type AuthGuardStatus = 'loading' | 'unauth' | 'blocked' | 'unverified' | 'auth';

interface RequireAuthProps {
  children: React.ReactNode;
}

export const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const { authStatus, isLoading } = useAuth();
  const location = useLocation();
  const [guardStatus, setGuardStatus] = React.useState<AuthGuardStatus>('loading');

  // Determine guard status based on auth state
  React.useEffect(() => {
    if (isLoading) {
      setGuardStatus('loading');
      return;
    }

    if (!authStatus?.authenticated) {
      setGuardStatus('unauth');
      return;
    }

    // Check if user account is inactive
    if (authStatus.user && authStatus.user.is_active === false) {
      setGuardStatus('blocked');
      return;
    }

    // Check if user email is not verified
    if (authStatus.user && authStatus.user.is_verified === false) {
      setGuardStatus('unverified');
      return;
    }

    // User is authenticated, active, and verified
    setGuardStatus('auth');
  }, [authStatus, isLoading]);

  // Focus management: focus title when status changes
  React.useEffect(() => {
    if (guardStatus === 'blocked') {
      const title = document.querySelector('.blocked-account-title') as HTMLElement;
      if (title) {
        title.focus();
      }
    }
  }, [guardStatus]);

  // Render based on guard status
  switch (guardStatus) {
    case 'loading':
      return (
        <div className="require-auth-loading" role="status" aria-live="polite">
          <div className="require-auth-spinner" aria-hidden="true" />
          <span className="require-auth-loading-text">Vérification de l'authentification...</span>
        </div>
      );

    case 'unauth':
      // Redirect to login, preserving the intended destination (full path including search and hash)
      return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />;

    case 'unverified':
      // Redirect to verify email page (only include email param if non-empty)
      const emailParam = authStatus?.user?.email ? `email=${encodeURIComponent(authStatus.user.email)}&` : '';
      return (
        <Navigate
          to={`/verify-email?${emailParam}pending=true`}
          replace
        />
      );

    case 'blocked':
      return (
        <div className="require-auth-shell">
          <TitleBar />
          <main className="require-auth-main">
            <BlockedAccountScreen />
          </main>
        </div>
      );

    case 'auth':
      return <>{children}</>;

    default:
      return null;
  }
};
